/**
 * 延后挂载 Apps section（如 Judge.me Cards Carousel）。
 *
 * App block 若直接输出带 src 的 <img>，preload scanner 会在首屏就拉取全部评论图。
 * 把内容放进 <template>，进视口附近再挂载。
 *
 * Judge.me 注意：
 * 1) 内联脚本依赖 jdgm.docReady；页面已 load 后再挂载时需改成 eager。
 * 2) video_carousel.js 会在加载时 querySelector 并 clone 卡片做 infinite loop；
 *    若当时还没有 .jdgm-media-card（评论 API 未返回），会在 ~2s 后放弃克隆，
 *    表现为只剩一张卡 + 大片空白、无法 loop。挂载后必须等卡片出现再补克隆/重定位。
 * 3) PDP review_widget 在 <template> 里时 loader 扫不到；挂载后必须以 type=module
 *    加载 data-entry-point（如 review_widget.js）。按普通 script 注入会报
 *    “Cannot use import statement outside a module”，只剩 SSR 评论 HTML、无样式。
 *
 * 全局 SKDeferred 已停用；若将来重新启用，挂载前仍应调用 flushForViewport。
 */
(function () {
  'use strict';

  var ROOT = '[data-tp-deferred-app]';
  var TEMPLATE = 'template[data-tp-deferred-app-content]';
  var FAILSAFE_MS = 4000;
  var CARD_WAIT_ATTEMPTS = 60;
  var CARD_WAIT_MS = 100;

  function patchJudgeMeDocReady() {
    if (!window.jdgm || typeof window.jdgm.docReady !== 'function') return;
    if (window.jdgm.__tpDocReadyPatched) return;
    window.jdgm.__tpDocReadyPatched = true;

    var original = window.jdgm.docReady.bind(window.jdgm);
    window.jdgm.docReady = function (fn) {
      if (typeof fn !== 'function') return original(fn);
      if (document.readyState === 'loading') return original(fn);
      try {
        fn();
      } catch (e) {
        /* ignore */
      }
    };
  }

  function moveStylesToHead(scope) {
    scope.querySelectorAll('link[rel="stylesheet"]').forEach(function (link) {
      if (link.dataset.tpMoved === 'true') return;
      link.dataset.tpMoved = 'true';
      document.head.appendChild(link);
    });
    scope.querySelectorAll('style').forEach(function (style) {
      if (style.dataset.tpMoved === 'true') return;
      style.dataset.tpMoved = 'true';
      document.head.appendChild(style);
    });
  }

  function runScripts(scope) {
    var scripts = Array.prototype.slice.call(scope.querySelectorAll('script'));
    scripts.forEach(function (oldScript) {
      var script = document.createElement('script');
      script.setAttribute('data-tp-speed-allow', 'true');
      Array.prototype.forEach.call(oldScript.attributes, function (attr) {
        script.setAttribute(attr.name, attr.value);
      });
      if (oldScript.textContent) script.textContent = oldScript.textContent;
      oldScript.parentNode.replaceChild(script, oldScript);
    });
  }

  function notifyJudgeMe(scope) {
    try {
      if (window.jdgm && typeof window.jdgm.initializeWidgets === 'function') {
        window.jdgm.initializeWidgets(scope);
      }
    } catch (e) {
      /* ignore */
    }
  }

  function getCarousel(root) {
    return root.querySelector('.jdgm-cards-carousel, .jdgm-videos-carousel');
  }

  function getRealCards(carousel) {
    if (!carousel) return [];
    var container = carousel.querySelector('.jdgm-videos-container');
    if (!container) return [];
    return Array.prototype.slice.call(container.querySelectorAll('.jdgm-media-card')).filter(function (card) {
      return !card.classList.contains('jdgm-clone-before') && !card.classList.contains('jdgm-clone-after');
    });
  }

  function carouselLoopReady(carousel) {
    if (!carousel || carousel.classList.contains('jdgm-hidden')) return false;
    if (!carousel._cloneCount) return false;
    var wrapper = carousel.querySelector('.jdgm-cards-wrapper');
    if (!wrapper) return false;
    return wrapper.getBoundingClientRect().width > 200;
  }

  function repositionCardsCarousel(carousel) {
    var container = carousel.querySelector('.jdgm-videos-container');
    var wrapper = carousel.querySelector('.jdgm-cards-wrapper');
    if (!container || !wrapper) return false;

    var active = container.querySelector('.jdgm-media-card.active');
    if (!active) {
      var cards = container.querySelectorAll('.jdgm-media-card');
      var idx = carousel._cloneCount || 0;
      active = cards[idx] || cards[0];
      if (active) {
        cards.forEach(function (card) {
          card.classList.remove('active');
        });
        active.classList.add('active');
      }
    }
    if (!active) return false;

    var wr = wrapper.getBoundingClientRect();
    var ar = active.getBoundingClientRect();
    if (wr.width < 50 || ar.width < 20) return false;

    var dx = wr.left - ar.left;
    container.style.transition = 'none';
    container.style.transform = 'translateX(' + dx + 'px)';
    // force reflow
    void container.offsetHeight;
    requestAnimationFrame(function () {
      container.style.transition = '';
      carousel._initialPositioningPending = false;
      carousel._initialPositioningComplete = true;
    });
    return true;
  }

  function hasCloneNodes(container) {
    return !!(container && container.querySelector('.jdgm-clone-before, .jdgm-clone-after'));
  }

  function ensureCardsCloned(carousel) {
    if (!carousel || !carousel.classList.contains('jdgm-cards-carousel')) return false;
    if (carousel._tpCloning) return false;
    var container = carousel.querySelector('.jdgm-videos-container');
    if (!container) return false;

    var realCards = getRealCards(carousel);
    if (!realCards.length) return false;

    carousel._isCardsCarousel = true;

    // Judge.me buildCards 可能在 clone 之后又 innerHTML 清空；flag 还在但节点没了
    if (carousel._cloneCount && hasCloneNodes(container)) {
      return repositionCardsCarousel(carousel);
    }

    carousel._tpCloning = true;

    // 清掉残留 clone / 过期 flag，避免重复叠加
    container.querySelectorAll('.jdgm-clone-before, .jdgm-clone-after').forEach(function (node) {
      node.remove();
    });
    delete carousel._cloneCount;
    delete carousel._originalCardCount;
    carousel._initialPositioningPending = false;
    carousel._initialPositioningComplete = false;

    try {
      if (
        window.jdgmCarouselCardsCloning &&
        typeof window.jdgmCarouselCardsCloning.cloneCards === 'function'
      ) {
        window.jdgmCarouselCardsCloning.cloneCards(carousel, container);
      }
    } catch (e) {
      /* ignore */
    }

    // Judge.me API 未就绪或未写入节点时，按同样规则本地补克隆（前后各最多 4 张）
    if (!hasCloneNodes(container)) {
      var cloneCount = Math.min(
        (window.jdgmCarouselCardsCloning && window.jdgmCarouselCardsCloning.CLONE_COUNT) || 4,
        realCards.length
      );
      carousel._originalCardCount = realCards.length;
      carousel._cloneCount = cloneCount;
      for (var i = cloneCount - 1; i >= 0; i--) {
        var before = realCards[realCards.length - cloneCount + i].cloneNode(true);
        before.classList.add('jdgm-clone-before');
        before.setAttribute('data-clone-type', 'before');
        container.insertBefore(before, container.firstChild);
      }
      for (var j = 0; j < cloneCount; j++) {
        var after = realCards[j].cloneNode(true);
        after.classList.add('jdgm-clone-after');
        after.setAttribute('data-clone-type', 'after');
        container.appendChild(after);
      }
      carousel._initialPositioningPending = true;
    }

    carousel._tpCloning = false;

    if (carousel._cloneCount && hasCloneNodes(container)) {
      requestAnimationFrame(function () {
        repositionCardsCarousel(carousel);
      });
      return true;
    }
    return false;
  }

  function loadVideoCarouselScript(carousel, done) {
    var finish = function () {
      if (typeof done === 'function') done();
    };

    try {
      if (window.jdgm && typeof window.jdgm.loadScript === 'function') {
        window.jdgm.loadScript('video_carousel.js');
      }
    } catch (e) {
      /* ignore */
    }

    // cloning API 已在且本节点已被 video_carousel 接管 → 无需再注入
    if (
      window.jdgmCarouselCardsCloning &&
      window.jdgmNextCard &&
      carousel &&
      carousel._videoCarouselInitialized
    ) {
      finish();
      return;
    }

    // API 已在但本节点从未被 IIFE 扫到：补一次 cache-busted 注入以跑自动 init
    // （同 URL 不会重新执行；带 tp_reinit 才会）
    if (window.jdgmCarouselCardsCloning && window.jdgmNextCard && carousel && !carousel._videoCarouselInitialized) {
      if (document.querySelector('script[data-tp-jdgm-video-carousel="true"]')) {
        finish();
        return;
      }
    } else if (window.jdgmCarouselCardsCloning && window.jdgmNextCard) {
      finish();
      return;
    }

    var src = judgeMeCdnBase().replace(/\/?$/, '/') + 'video_carousel.js';
    var script = document.createElement('script');
    script.src = src + (src.indexOf('?') >= 0 ? '&' : '?') + 'tp_reinit=' + Date.now();
    script.async = true;
    script.dataset.tpJdgmVideoCarousel = 'true';
    script.onload = finish;
    script.onerror = finish;
    document.head.appendChild(script);
  }

  function bindLayoutFix(carousel) {
    if (!carousel || carousel.dataset.tpLayoutFixBound === 'true') return;
    carousel.dataset.tpLayoutFixBound = 'true';

    var wrapper = carousel.querySelector('.jdgm-cards-wrapper');
    var container = carousel.querySelector('.jdgm-videos-container');

    if (wrapper && typeof ResizeObserver === 'function') {
      var ro = new ResizeObserver(function () {
        if (!carousel._cloneCount || !hasCloneNodes(container)) {
          ensureCardsCloned(carousel);
          return;
        }
        repositionCardsCarousel(carousel);
      });
      ro.observe(wrapper);
      carousel._tpLayoutRo = ro;
    }

    // buildCards 用 innerHTML 重写容器时，补一次克隆
    if (container && typeof MutationObserver === 'function') {
      var mo = new MutationObserver(function () {
        if (getRealCards(carousel).length && (!carousel._cloneCount || !hasCloneNodes(container))) {
          ensureCardsCloned(carousel);
        }
      });
      mo.observe(container, { childList: true });
      carousel._tpCardsMo = mo;
    }
  }

  function finalizeJudgeMeCarousel(root, attempt) {
    attempt = attempt || 0;
    var carousel = getCarousel(root);
    if (!carousel) {
      if (attempt < CARD_WAIT_ATTEMPTS) {
        setTimeout(function () {
          finalizeJudgeMeCarousel(root, attempt + 1);
        }, CARD_WAIT_MS);
      }
      return;
    }

    notifyJudgeMe(root);

    var realCards = getRealCards(carousel);
    if (!realCards.length) {
      try {
        if (window.jdgm && typeof window.jdgm.loadScript === 'function') {
          window.jdgm.loadScript('video_carousel.js');
        }
      } catch (e) {
        /* ignore */
      }
      if (attempt < CARD_WAIT_ATTEMPTS) {
        setTimeout(function () {
          finalizeJudgeMeCarousel(root, attempt + 1);
        }, CARD_WAIT_MS);
      }
      return;
    }

    loadVideoCarouselScript(carousel, function () {
      ensureCardsCloned(carousel);

      // 若 script 刚加载且自动 init 成功，仍可能因宽度为 0 定位失败
      if (!carouselLoopReady(carousel) && attempt < CARD_WAIT_ATTEMPTS) {
        setTimeout(function () {
          ensureCardsCloned(carousel);
          if (!carouselLoopReady(carousel)) {
            finalizeJudgeMeCarousel(root, attempt + 1);
          } else {
            bindLayoutFix(carousel);
          }
        }, CARD_WAIT_MS);
        return;
      }

      bindLayoutFix(carousel);
      repositionCardsCarousel(carousel);

      try {
        if (carousel._videoCarouselInstance && typeof carousel._videoCarouselInstance.syncActiveCard === 'function') {
          carousel._videoCarouselInstance.syncActiveCard();
        }
      } catch (e2) {
        /* ignore */
      }
    });
  }

  function judgeMeCdnBase() {
    return (
      (window.jdgm && window.jdgm.CDN_BASE_URL) ||
      'https://cdn.shopify.com/extensions/019ff796-6653-730b-8f97-9663ce92337a/judgeme-700/assets/'
    );
  }

  function loadJudgeMeEntryModule(entryPoint, done) {
    var finish = function () {
      if (typeof done === 'function') done();
    };

    if (!entryPoint) {
      finish();
      return;
    }

    var marker = 'script[data-tp-jdgm-entry="' + entryPoint.replace(/"/g, '') + '"]';
    if (document.querySelector(marker)) {
      finish();
      return;
    }

    var src = judgeMeCdnBase().replace(/\/?$/, '/') + entryPoint;
    var script = document.createElement('script');
    // 必须先 allow 再设 src：tp-speed-defer 会拦截 createElement('script').src，
    // 若此时未 allow，只会写下 data-tp-speed-pending-src、真实 src 为空，module 永不执行。
    script.setAttribute('data-tp-speed-allow', 'true');
    script.type = 'module';
    script.async = true;
    script.dataset.tpJdgmEntry = entryPoint;
    script.onload = finish;
    script.onerror = finish;
    script.src = src + (src.indexOf('?') >= 0 ? '&' : '?') + 'tp_entry=' + Date.now();
    document.head.appendChild(script);
  }

  function reviewWidgetReady(root) {
    return !!root.querySelector('.jm-review-widget, .jdgm-rev-widg.jdgm--done-setup-widget');
  }

  function finalizeJudgeMeReviewWidget(root, attempt) {
    attempt = attempt || 0;
    var widgets = root.querySelectorAll('.jdgm-review-widget[data-entry-point]');
    if (!widgets.length) return;

    if (reviewWidgetReady(root)) {
      notifyJudgeMe(root);
      return;
    }

    if (attempt === 0) {
      var entryPoints = Object.create(null);
      widgets.forEach(function (widget) {
        var entry = widget.getAttribute('data-entry-point');
        if (entry) entryPoints[entry] = true;
      });
      Object.keys(entryPoints).forEach(function (entry) {
        loadJudgeMeEntryModule(entry);
      });
    }

    notifyJudgeMe(root);

    if (attempt < 40 && !reviewWidgetReady(root)) {
      setTimeout(function () {
        finalizeJudgeMeReviewWidget(root, attempt + 1);
      }, 200);
    }
  }

  function releaseThirdPartyInterceptor() {
    try {
      if (window.TPSpeedDefer && typeof window.TPSpeedDefer.flushMatching === 'function') {
        window.TPSpeedDefer.flushMatching(
          /carousels|media_carousel|judgeme|judge\.me|video_carousel|review_widget|review-widget/i
        );
      }
    } catch (e1) {
      /* ignore */
    }
    try {
      if (!window.SKDeferredScripts) return;
      if (typeof window.SKDeferredScripts.flushForViewport === 'function') {
        window.SKDeferredScripts.flushForViewport();
      } else if (typeof window.SKDeferredScripts.flush === 'function') {
        window.SKDeferredScripts.flush();
      }
    } catch (e) {
      /* ignore */
    }
  }

  function mount(root) {
    if (!root || root.dataset.tpDeferredAppMounted === 'true') return;

    var template = root.querySelector(TEMPLATE);
    if (!template) {
      root.dataset.tpDeferredAppMounted = 'true';
      root.classList.add('tp-deferred-app--mounted');
      root.removeAttribute('aria-busy');
      return;
    }

    releaseThirdPartyInterceptor();
    patchJudgeMeDocReady();

    var hint = root.querySelector('[data-tp-deferred-app-hint]');
    if (hint) hint.remove();

    root.appendChild(template.content);
    template.remove();
    root.dataset.tpDeferredAppMounted = 'true';
    root.classList.add('tp-deferred-app--mounted');
    root.removeAttribute('aria-busy');

    moveStylesToHead(root);
    runScripts(root);
    notifyJudgeMe(root);
    finalizeJudgeMeReviewWidget(root, 0);
    finalizeJudgeMeCarousel(root, 0);

    if (window.TPImage && typeof window.TPImage.observe === 'function') {
      window.TPImage.observe(root);
    }
  }

  function observe(root) {
    if (!root || root.dataset.tpDeferredAppReady === 'true') return;
    root.dataset.tpDeferredAppReady = 'true';

    if (window.Shopify && Shopify.designMode) {
      mount(root);
      return;
    }

    if (!('IntersectionObserver' in window)) {
      mount(root);
      return;
    }

    var done = false;
    function mountOnce() {
      if (done) return;
      done = true;
      mount(root);
    }

    var io = new IntersectionObserver(
      function (entries) {
        if (!entries.some(function (entry) {
          return entry.isIntersecting;
        })) {
          return;
        }
        io.disconnect();
        mountOnce();
      },
      { rootMargin: '400px 0px' }
    );
    io.observe(root);

    var rect = root.getBoundingClientRect();
    if (rect.top < window.innerHeight + 400 && rect.bottom > -400) {
      io.disconnect();
      mountOnce();
    }

    function nearViewport() {
      var next = root.getBoundingClientRect();
      return next.top < window.innerHeight + 600 && next.bottom > -200;
    }

    function onScrollFailsafe() {
      if (done) {
        window.removeEventListener('scroll', onScrollFailsafe);
        return;
      }
      if (nearViewport()) {
        window.removeEventListener('scroll', onScrollFailsafe);
        io.disconnect();
        mountOnce();
      }
    }

    window.addEventListener('scroll', onScrollFailsafe, { passive: true });

    var armTimeoutFailsafe = function () {
      setTimeout(function () {
        if (done) return;
        if (nearViewport()) {
          io.disconnect();
          window.removeEventListener('scroll', onScrollFailsafe);
          mountOnce();
        }
      }, FAILSAFE_MS);
    };
    if (document.readyState === 'complete') {
      armTimeoutFailsafe();
    } else {
      window.addEventListener('load', armTimeoutFailsafe, { once: true });
    }
  }

  function init(scope) {
    var ctx = scope || document;
    if (!ctx.querySelectorAll) return;
    ctx.querySelectorAll(ROOT).forEach(observe);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      init(document);
    });
  } else {
    init(document);
  }

  document.addEventListener('shopify:section:load', function (event) {
    init(event.target);
  });
})();
